import { isEmpty } from 'lodash';
import { capturePosthogEvent, captureTypedPosthogEvent, POSTHOG_EVENTS } from '~/common/posthogEvents';
import type { TOCChild, TOCItem, TOCParent } from '~/common/types';
import { writeCustomDataVisibility } from '~/pages/projectDetails/map/customDataVisibility';
import layerStore, { CUSTOM_DATA_SECTION_ID, isTOCParent } from '~/store2/layers';
import { collectLeaves } from './sharedUtils';

// ---------------------------------------------------------------------------
// Write path for the "Custom Data" picker section (CustomDataTOC).
// Custom-data ids are per-project, so they must NEVER enter the persisted
// userLayerConfig document: every write here goes straight to the shared render
// set (`layerStore.visibleLayerIds`) or the session-only `expandedSections`
// set — the paths the debounced document sync never observes — and never
// through the layerConfigStore facade. Durable on/off state instead lives in a
// project-scoped localStorage record, written by persistCustomDataVisibility.
// ---------------------------------------------------------------------------

export const CUSTOM_DATA_SECTION_LABEL = 'Custom Data';

// Module-level (not per-mount): the TOC panel unmounts whenever it closes, so a per-mount
// default would re-expand a section the user explicitly collapsed earlier in the session.
let hasSeededDefaultExpansion = false;

/**
 * Default the section expanded, matching the legacy injection's `expanded: true`. At most once
 * per session — after the first seed, expansion is owned by the user's toggles (and the
 * expand/collapse-all carry in layerConfigStore). Direct set write (no analytics): a default,
 * not a user gesture.
 */
export function seedCustomDataSectionDefaultExpansion(): void {
  if (hasSeededDefaultExpansion) return;
  hasSeededDefaultExpansion = true;
  layerStore.expandedSections.add(CUSTOM_DATA_SECTION_ID);
}

/** Test-only: re-arm the once-per-session default-expansion seed. */
export function resetCustomDataSectionDefaultExpansionSeed(): void {
  hasSeededDefaultExpansion = false;
}

/**
 * Record which of the project's custom-data leaves are on. The single write point: every durable
 * gesture routes through here, so "nothing to record yet" is decided once.
 *
 * `projectId` is undefined whenever the remember-visibility feature is off, which is also what keeps
 * such a session from writing. An empty group means the rows query hasn't resolved — there is nothing on screen to
 * act on, so a gesture in that window records nothing rather than marking the project visited. The
 * two store flags belong to the download form: while it owns map visibility its wholesale wipes
 * must not reach the record.
 */
export function persistCustomDataVisibility(projectId: string | undefined): void {
  if (!projectId || layerStore.pausePersistence || layerStore.suppressCustomDataDefaultOn) return;
  const leafIds = collectLeaves(layerStore.customDataTOCGroup).map((leaf) => leaf.id);
  if (leafIds.length === 0) return;
  writeCustomDataVisibility(
    projectId,
    leafIds.filter((id) => layerStore.visibleLayerIds.has(id)),
  );
}

export function toggleCustomDataLeaf(
  leafId: string,
  visible: boolean,
  context: { projectId?: string; organizationId?: string } = {},
  persistProjectId?: string,
): void {
  if (visible) layerStore.visibleLayerIds.add(leafId);
  else layerStore.visibleLayerIds.delete(leafId);
  persistCustomDataVisibility(persistProjectId);
  capturePosthogEvent(POSTHOG_EVENTS.layerToggled, {
    layer_id: leafId,
    action: visible ? 'on' : 'off',
  });
  // Parity with the legacy picker's leaf rows: project-scoped toggles also emit the
  // project filter event (custom data only renders on a project map).
  if (context.projectId) {
    captureTypedPosthogEvent(POSTHOG_EVENTS.projectLayerFilterChanged, {
      project_id: context.projectId,
      organization_id: context.organizationId,
      filter_type: 'layer_visibility',
      value: leafId,
      action: visible ? 'add' : 'remove',
    });
  }
}

/** Shared bulk write behind both section-shaped toggles, so their event shape matches by construction. */
function toggleLeavesVisible(
  sectionLabel: string,
  children: readonly TOCChild[],
  visible: boolean,
  persistProjectId?: string,
): void {
  const childLayerIds = children.map((c) => c.id);
  for (const id of childLayerIds) {
    if (visible) layerStore.visibleLayerIds.add(id);
    else layerStore.visibleLayerIds.delete(id);
  }
  persistCustomDataVisibility(persistProjectId);
  capturePosthogEvent(POSTHOG_EVENTS.layerSectionToggled, {
    section_label: sectionLabel,
    action: visible ? 'on' : 'off',
    child_layer_ids: childLayerIds,
  });
}

export function toggleCustomDataSection(
  children: readonly TOCChild[],
  visible: boolean,
  persistProjectId?: string,
): void {
  toggleLeavesVisible(CUSTOM_DATA_SECTION_LABEL, children, visible, persistProjectId);
}

/**
 * Bulk toggle for one split upload's parent row (every category leaf under it). Section-shaped
 * gesture, so it emits the section event labelled with the upload — no per-leaf project filter
 * events, matching the section toggle and the legacy picker's `handleLayerSectionToggle`.
 */
export function toggleCustomDataUpload(parent: TOCParent, visible: boolean, persistProjectId?: string): void {
  toggleLeavesVisible(parent.label, collectLeaves(parent.children), visible, persistProjectId);
}

function setSectionExpanded(sectionId: string, expanded: boolean): void {
  if (expanded) layerStore.expandedSections.add(sectionId);
  else layerStore.expandedSections.delete(sectionId);
  captureTypedPosthogEvent(POSTHOG_EVENTS.layerSectionExpanded, { section_id: sectionId, expanded });
}

export function toggleCustomDataSectionExpanded(expanded: boolean, isSearching = false): void {
  // During search the section renders force-expanded; writing the stored key would only take
  // effect after the search clears, reading as an unexplained collapse. Ignore the click.
  if (isSearching) return;
  setSectionExpanded(CUSTOM_DATA_SECTION_ID, expanded);
}

/** Expansion for one split upload's parent row, keyed by its `customData:<rowId>` sectionId. */
export function toggleCustomDataUploadExpanded(sectionId: string, expanded: boolean): void {
  setSectionExpanded(sectionId, expanded);
}

/** Expansion + testid key for an upload row: its own sectionId, label only as a defensive fallback. */
export function customDataUploadKey(parent: TOCParent): string {
  return parent.sectionId ?? parent.label;
}

/**
 * The upload chevron's write: flip whatever the row is currently rendering. Keyed off the same
 * helper as the row's testid, so the click can't drift from the row. No-op during search: the row
 * is force-expanded at render time, so the flip would only land after the search clears.
 */
export function toggleCustomDataUploadExpandedFromRow(
  parent: TOCParent,
  currentlyExpanded: boolean,
  isSearching = false,
): void {
  if (isSearching) return;
  toggleCustomDataUploadExpanded(customDataUploadKey(parent), !currentlyExpanded);
}

// Mirrors the picker's case-insensitive substring match. TOC.tsx and MetadataPicker.tsx each keep a
// private copy of it; importing TOC.tsx here would cycle back through CustomDataTOC.
function matchSearch(label: string, searchTerm: string): boolean {
  return label.toLowerCase().includes(searchTerm.toLowerCase());
}

/** One rendered row: a flat leaf, or a split upload with the category leaves that survived search. */
export type CustomDataTOCRow =
  | { kind: 'leaf'; leaf: TOCChild }
  | { kind: 'upload'; parent: TOCParent; children: TOCChild[] };

/**
 * Search filtering for the Custom Data section, mirroring the legacy picker: a matching section or
 * upload label shows everything beneath it, otherwise only matching leaves survive and an upload
 * with no match drops out. An empty result means the section renders nothing. Pure — expansion is
 * forced at render time, never written here.
 */
export function filterCustomDataTOCRows(items: readonly TOCItem[], searchTerm: string): CustomDataTOCRow[] {
  const toRow = (item: TOCItem): CustomDataTOCRow =>
    isTOCParent(item)
      ? { kind: 'upload', parent: item, children: collectLeaves(item.children) }
      : { kind: 'leaf', leaf: item };

  if (isEmpty(searchTerm) || matchSearch(CUSTOM_DATA_SECTION_LABEL, searchTerm)) return items.map(toRow);

  const rows: CustomDataTOCRow[] = [];
  for (const item of items) {
    if (!isTOCParent(item)) {
      if (matchSearch(item.label, searchTerm)) rows.push({ kind: 'leaf', leaf: item });
      continue;
    }
    const leaves = collectLeaves(item.children);
    const children = matchSearch(item.label, searchTerm)
      ? leaves
      : leaves.filter((leaf) => matchSearch(leaf.label, searchTerm));
    if (children.length > 0) rows.push({ kind: 'upload', parent: item, children });
  }
  return rows;
}
